import React, { useState, useEffect } from 'react';
import { post } from '@aws-amplify/api';
import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useUser } from '../utils/hooks/useUser';

const Home = () => {
  const navigate = useNavigate();
  const { userEmail, userId } = useUser();
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    resume: '',
    jobDescription: '',
    personalDataOption: 'profile' // 'pasted' or 'profile'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumeHistory, setResumeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    template: false,
    resume: false,
    jobDescription: false
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set default template when component mounts
  useEffect(() => {
    if (userEmail && userId) {
      // Set a default template ID
      setSelectedTemplate('template');
    }
  }, [userEmail, userId]);

  
  // Fetch resume history when history tab is selected
  useEffect(() => {
    if (activeTab === 'history' && userEmail) {
      fetchResumeHistory();
    }
  }, [activeTab, userEmail]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Check if userId is available
      if (!userId) {
        throw new Error('User ID not available. Please refresh the page and try again.');
      }

      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      // First check quota availability for resume processing
      const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/check-subscription`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({ 
          user_id: userId,
          email: userEmail,
          action: 'validate_quota',
          quota_type: 'resume'
        })
      });

      console.log('Quota validation request:', { userId, userEmail });

      const quotaData = await subscriptionResponse.json();
      
      if (!quotaData.hasQuota) {
        // If no quota available, show error message
        setError('No quota remaining for resume optimization. Please upgrade your subscription to continue.');
        return;
      }

      console.log(`Quota available: ${quotaData.remainingQuota} remaining`);
  

      // If subscription is active, proceed with resume processing
      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/improve-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({
          RS: formData.personalDataOption === 'pasted' ? formData.resume : '',
          JD: formData.jobDescription,
          email: userEmail,
          template_id: selectedTemplate,
          resume_from_profile: formData.personalDataOption === 'profile'
        })
      });

      console.log('Raw response:', response);
      const responseText = await response.text();
      console.log('Response text:', responseText);

      let responseBody;
      try {
        responseBody = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format from server');
      }

      console.log('Parsed Response Body:', responseBody);

      if (!responseBody.resultId) {
        console.error('Missing resultId in response:', responseBody);
        throw new Error('Invalid response format from server');
      }

      const resultId = responseBody.resultId;
      console.log('Retrieved ResultID:', resultId);
      
      localStorage.setItem('userEmail', userEmail);

      navigate(`/results?submission_id=${resultId}`);

    } catch (error) {
      console.error('API Error:', error);
      if (error.response) {
        console.error('Error Response Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          body: await error.response.body?.text()
        });
      }
      setError(`Failed to process resume: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    // Fetch templates only when template section is expanded
    if (section === 'template' && !expandedSections.template) {
      fetchTemplates();
    }
    
    // Auto-scroll to selected template when template section opens
    if (section === 'template' && !expandedSections.template) {
      setTimeout(() => scrollToSelectedTemplate(), 100);
    }
  };

  const handleTemplateSelect = (templateName) => {
    setSelectedTemplate(templateName);
  };

  const handleImageClick = (imageUrl, templateName) => {
    setModalImage({ url: imageUrl, name: templateName });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
  };

  const fetchTemplates = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,GET'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchResumeHistory = async () => {
    try {
      setHistoryLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/history/resume`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,GET'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResumeHistory(data.history || []);
    } catch (error) {
      console.error('Error fetching resume history:', error);
      setError('Failed to load resume history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const scrollToSelectedTemplate = () => {
    if (selectedTemplate) {
      const templateElements = document.querySelectorAll('[data-template]');
      const selectedElement = Array.from(templateElements).find(
        el => el.getAttribute('data-template') === selectedTemplate
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Improve Your Resume</h1>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('create')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'create'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Create New Resume
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Review Past Resumes
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'create' && (
        <>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('template')}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center border-b border-gray-200"
              >
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-700">
                    Choose Template
                  </label>
                  {selectedTemplate && (
                    <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      Clean Minimalist
                    </span>
                  )}
                </div>
                <span className="text-gray-500">
                  {expandedSections.template ? '▼' : '▶'}
                </span>
              </button>
              {expandedSections.template && (
                <div className="p-4">
                  {templatesLoading ? (
                    <div className="flex justify-center py-4">
                      <p className="text-gray-500">Loading templates...</p>
                    </div>
                  ) : templates.length > 0 ? (
                    <div className="flex overflow-x-auto space-x-4 pb-2">
                      {templates.map((template) => (
                        <div 
                          key={template.template_id}
                          className={`border-2 rounded-lg p-3 cursor-pointer flex-shrink-0 min-w-[120px] ${
                            selectedTemplate === template.template_id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-blue-500'
                          }`}
                          onClick={() => handleTemplateSelect(template.template_id)}
                          data-template={template.template_id}
                        >
                          <div className="text-center">
                            {template.thumbnail_url ? (
                              <img 
                                src={template.thumbnail_url} 
                                alt={template.name}
                                className="w-16 h-20 mx-auto mb-2 rounded object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(template.thumbnail_url, template.name)}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <div className={`w-16 h-20 bg-gray-100 mx-auto mb-2 rounded ${template.thumbnail_url ? 'hidden' : ''}`}></div>
                            <p className="text-sm font-medium">{template.name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No templates available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('resume')}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center border-b border-gray-200"
              >
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-700">
                    My current resume
                  </label>
                  {formData.personalDataOption && (
                    <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      {formData.personalDataOption === 'profile' ? 'From Profile' : 'Pasted Content'}
                    </span>
                  )}
                </div>
                <span className="text-gray-500">
                  {expandedSections.resume ? '▼' : '▶'}
                </span>
              </button>
              {expandedSections.resume && (
                <div className="p-4">
                  <div className="space-y-2 mb-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="personalDataOption"
                        value="profile"
                        checked={formData.personalDataOption === 'profile'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-green-600">✓</span> Use resume from{' '}
                        <a 
                          href="/profile" 
                          className="text-blue-600 hover:text-blue-800 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          profile
                        </a>
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="personalDataOption"
                        value="pasted"
                        checked={formData.personalDataOption === 'pasted'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <span className="text-sm flex items-center group">
                        Paste new resume
                        <div className="relative ml-1">
                          <svg className="w-4 h-4 text-yellow-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-yellow-100 border border-yellow-300 rounded-lg shadow-lg text-xs text-gray-700 max-w-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                            If you paste phone, email, name etc. in the pasted content, it will be processed by AI. Recommended to use profile so your PII is not processed by AI.
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-yellow-300"></div>
                          </div>
                        </div>
                      </span>
                    </label>
                  </div>
                  {formData.personalDataOption === 'pasted' && (
                    <textarea
                      value={formData.resume}
                      onChange={handleInputChange}
                      name="resume"
                      rows={10}
                      className="w-full p-2 border rounded-md"
                      required
                    />
                  )}
                </div>
              )}
            </div>
            
            <div className="rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('jobDescription')}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center border-b border-gray-200"
              >
                <label className="text-sm font-medium text-gray-700">
                  Paste the job description
                </label>
                <span className="text-gray-500">
                  {expandedSections.jobDescription ? '▼' : '▶'}
                </span>
              </button>
              {expandedSections.jobDescription && (
                <div className="p-4">
                  <textarea
                    value={formData.jobDescription}
                    onChange={handleInputChange}
                    name="jobDescription"
                    rows={10}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isLoading || !userEmail || !selectedTemplate}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              style={{ backgroundColor: '#2563eb' }}
            >
              {isLoading ? 'Processing...' : 'Submit'}
            </button>
          </form>
          {error && <p className="text-red-500 mt-4">{error}</p>}
        </>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          {historyLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading resume history...</p>
            </div>
          ) : resumeHistory.length > 0 ? (
            <div className="space-y-4">
              {resumeHistory.map((resume, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Resume #{resumeHistory.length - index}
                      </h4>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Created on:</span> {new Date(resume.SubmissionDateTime).toLocaleDateString()}
                        </p>
                        {/* {resume.jobDescription && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Job Description used:</span> <a href={resume.jobDescription} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">View JD</a>
                          </p>
                        )} */}
                        {/* {resume.RS_Link && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Original resume:</span> <a href={resume.RS_Link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">View</a>
                          </p>
                        )} */}
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Status:</span> 
                          {resume.status === 'PREVIEW CONTENT' ? (
                            <button
                              onClick={() => navigate(`/results?submission_id=${resume.resultId}`)}
                              className="ml-1 px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors"
                            >
                              {resume.status}
                            </button>
                          ) : (
                            <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                              resume.status === 'completed' ? 'bg-green-100 text-green-700' :
                              resume.status === 'failed' ? 'bg-red-100 text-red-700' :
                              resume.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {resume.status || 'Unknown'}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">New resume tailored to job description:</span> 
                          {resume.Results_Link ? (
                            <a href={resume.Results_Link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Download</a>
                          ) : (
                            <span className="text-gray-400">Document creation feature coming soon!</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Past Resumes</h3>
              <p className="text-gray-600 mb-6">View and manage your previously created resumes.</p>
              <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-sm text-gray-500 text-center">No past resumes found. Create your first resume to see it here.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Image Modal */}
      {isModalOpen && modalImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold z-10"
            >
              ×
            </button>
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-center">{modalImage.name}</h3>
              <img
                src={modalImage.url}
                alt={modalImage.name}
                className="max-w-full max-h-[70vh] mx-auto rounded shadow-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<p class="text-red-500 text-center">Image failed to load</p>';
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 